import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarSearch } from 'lucide-react';
import { formatDate } from '../../utils/formatDate';

interface NoAvailabilityGuidanceProps {
  readonly apartmentId: string;
  readonly startDate: string;
  readonly endDate: string;
}

/**
 * Guidance shown for `NO_AVAILABILITY` conflicts (US5) — these have no resolve/dismiss action
 * (FR-007: resolution happens implicitly via a later sync once availability exists). Renders an
 * explanation plus a link that deep-links back to the affected apartment's Availabilities calendar,
 * scoped to the conflicting date range via route state the calendar view already understands.
 */
export const NoAvailabilityGuidance = ({
  apartmentId,
  startDate,
  endDate,
}: NoAvailabilityGuidanceProps) => {
  const { t } = useTranslation(['pages']);
  const navigate = useNavigate();

  const handleGoToCalendar = () => {
    navigate(`/apartments/${apartmentId}/availabilities`, {
      state: { viewMode: 'calendar', prefillStartDate: startDate },
    });
  };

  return (
    <div className="mt-2 text-sm text-gray-600" data-testid="no-availability-guidance">
      <p>
        {t('pages:noAvailabilityConflictGuidance', {
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
        })}
      </p>
      <button
        type="button"
        onClick={handleGoToCalendar}
        className="mt-2 inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium"
      >
        <CalendarSearch size={16} />
        {t('pages:goToAvailabilityCalendar')}
      </button>
    </div>
  );
};
